import { AlertTriangle, Check, Loader2, Scissors } from "lucide-react";
import { STEM_PHASES } from "@/lib/studioApi";

export const SPLITS = [
  { parts: 4, title: "4 parts", body: "Vocals · Drums · Bass · Everything else", hint: "Quickest, cleanest split" },
  { parts: 6, title: "6 parts", body: "Vocals · Drums · Bass · Piano · Guitar · Everything else", hint: "More control — keys and guitar on their own" },
];

/** Choose how finely to split; a ready badge shows which splits this song already has. */
export function PartsPicker({ parts, onPick, sets, disabled }) {
  return (
    <div role="radiogroup" data-testid="stems-parts-picker" className="grid gap-3 sm:grid-cols-2">
      {SPLITS.map((o) => {
        const s = sets.find((x) => x.parts === o.parts);
        return (
          <button key={o.parts} type="button" role="radio" aria-checked={parts === o.parts} data-testid={`stems-parts-${o.parts}`} disabled={disabled} onClick={() => onPick(o.parts)}
            className={`rounded-3xl border p-4 text-left transition-colors duration-200 disabled:opacity-50 ${parts === o.parts ? "border-cyan-300/60 bg-cyan-500/[0.12]" : "border-white/10 bg-white/[0.03] hover:bg-white/[0.07]"}`}>
            <p className="flex items-center justify-between font-display text-lg font-semibold">
              {o.title}
              {s?.status === "done" ? <span className="inline-flex items-center gap-1 rounded-full border border-emerald-400/40 bg-emerald-500/10 px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider text-emerald-200"><Check className="h-3 w-3" />Ready</span> : null}
              {s?.status === "running" ? <Loader2 className="h-4 w-4 animate-spin text-cyan-200" /> : null}
            </p>
            <p className="mt-1 text-[13px] text-white/75">{o.body}</p>
            <p className="mt-0.5 text-[11px] text-white/45">{o.hint}</p>
          </button>
        );
      })}
    </div>
  );
}

/** A re-split in progress (or one that failed) — the finished parts below stay playable and downloadable. */
export function ResplitNotice({ pending, parts }) {
  const failed = pending.status === "error";
  return (
    <p data-testid="stems-resplit-notice" data-status={pending.status}
      className={`flex items-start gap-2 rounded-2xl border px-4 py-3 text-[13px] ${failed ? "border-rose-400/40 bg-rose-500/10 text-rose-100" : "border-cyan-400/30 bg-cyan-500/[0.08] text-cyan-100"}`}>
      {failed ? <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> : <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin" />}
      {failed ? `The new split didn't finish (${pending.error}) — your earlier ${parts} parts are still here.`
        : `Splitting again · ${STEM_PHASES[pending.phase] || "Working"}… Your current parts stay here until the new ones are ready.`}
    </p>
  );
}

/** Not split yet / splitting now / it failed — with the Split button. */
export function SplitPanel({ set, parts, onSplit, busy, instrumental }) {
  if (set?.status === "running") {
    return (
      <div data-testid="stems-progress" data-phase={set.phase} className="rounded-3xl border border-cyan-400/30 bg-cyan-500/[0.08] p-6">
        <p className="flex items-center gap-2 text-sm text-cyan-100"><Loader2 className="h-4 w-4 animate-spin" /> <span data-testid="stems-phase">{STEM_PHASES[set.phase] || "Working"}…</span>
          {set.phase === "saving" ? <span className="font-mono text-[11px] text-cyan-200/70">{set.saved || 0} of {parts}</span> : null}</p>
        <p className="mt-2 text-[12px] text-white/55">Usually about a minute. You can leave this page — the parts keep coming and are saved with the song.</p>
      </div>
    );
  }
  return (
    <div data-testid="stems-split-panel" className="rounded-3xl border border-white/[0.08] bg-[#0a0d1c]/80 p-6">
      {set?.status === "error" ? <p data-testid="stems-error" className="mb-4 flex items-start gap-2 rounded-2xl border border-rose-400/40 bg-rose-500/10 px-4 py-3 text-[13px] text-rose-100"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />{set.error}</p> : null}
      <p className="font-display text-lg font-semibold">Split this song into {parts} parts</p>
      <p className="mt-1.5 text-[13px] text-white/60">Each part is saved in full quality, so you can download it as WAV for a mixing engineer or MP3 for sharing.{instrumental ? " This song is instrumental, so the vocal part will be nearly silent." : ""}</p>
      <button type="button" data-testid="stems-split-btn" onClick={onSplit} disabled={busy}
        className="mt-5 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-cyan-400 to-blue-500 px-6 py-3 font-display text-sm font-semibold text-[#05070f] disabled:opacity-40">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Scissors className="h-4 w-4" />} {set?.status === "error" ? "Try again" : `Split into ${parts} parts`}
      </button>
    </div>
  );
}
